import { IMAGES } from "@/lib/constants";
import { Link } from "wouter";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import AnimatedSection from "@/components/AnimatedSection";
import LazyImage from "@/components/LazyImage";
import { blogPosts } from "@/lib/blogData";

const pillarCategories = [
  { name: "Laserskärning", href: "/kunskapsbank/laserskarning", color: "bg-blue-600" },
  { name: "Kantpressning", href: "/kunskapsbank/kantpressning", color: "bg-green-600" },
  { name: "Svetsning", href: "/kunskapsbank/svetsning", color: "bg-orange-600" },
  { name: "Material & Stål", href: "/kunskapsbank/material", color: "bg-purple-600" },
  { name: "Branschlösningar", href: "/kunskapsbank/branscher", color: "bg-emerald-700" },
  { name: "Praktiska guider", href: "/kunskapsbank/guider", color: "bg-red-700" },
  { name: "Framtid & Innovation", href: "/kunskapsbank/framtid", color: "bg-indigo-700" },
];

export default function Nyheter() {
  return (
    <Layout>
      <SEOHead
        title="Kunskapsbank & Nyheter | ASPAB"
        description="ASPAB:s kunskapsbank med artiklar om laserskärning, kantpressning, svetsning, material och branschlösningar. Fördjupa dig i teknik, guider och nyheter."
        canonical="https://aspab.com/nyheter"
        path="/nyheter"
        keywords="ASPAB kunskapsbank, laserskärning artiklar, metallbearbetning guider, plåtbearbetning nyheter"
        breadcrumbs={[{ name: "Kunskapsbank", href: "/nyheter" }]}
        ogImage={IMAGES.ogNyheter}
      />

      <PageHero
        title="Kunskapsbank"
        subtitle="Artiklar, guider och nyheter om laserskärning, kantpressning, svetsning och metallbearbetning"
        breadcrumbs={[{ name: "Kunskapsbank", href: "/nyheter" }]}
      />

      {/* Pillar page navigation */}
      <section className="bg-gray-50 py-12 md:py-16 border-b border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Utforska per ämne
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {pillarCategories.map((pillar) => (
                  <Link key={pillar.href} href={pillar.href} className="group block">
                    <div className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#2563EB]/30 hover:shadow-md transition-all duration-300">
                      <div className={`w-3 h-3 rounded-full ${pillar.color} shrink-0`} />
                      <span className="font-medium text-gray-900 group-hover:text-[#2563EB] transition-colors text-sm">
                        {pillar.name}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-[#2563EB] ml-auto transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* All articles listing */}
      <section className="bg-white py-16 md:py-24">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Alla artiklar
              </h2>
            </AnimatedSection>
            <div className="space-y-8">
              {blogPosts.map((post, i) => (
                <AnimatedSection key={post.slug} delay={Math.min(i * 50, 500)}>
                  <Link href={`/nyheter/${post.slug}`} className="group block">
                    <div className="grid md:grid-cols-5 gap-6 bg-white rounded-xl p-5 md:p-6 border border-gray-200 hover:border-[#2563EB]/30 hover:shadow-lg transition-all duration-300">
                      <div className="md:col-span-2">
                        <div className="aspect-[4/3] overflow-hidden rounded-lg">
                          <LazyImage
                            src={post.image}
                            alt={post.imageAlt}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            wrapperClassName="w-full h-full"
                          />
                        </div>
                      </div>
                      <div className="md:col-span-3 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">
                            {post.category}
                          </span>
                          <span className="flex items-center gap-1.5 text-gray-500 text-xs">
                            <Calendar className="w-3.5 h-3.5" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1.5 text-gray-500 text-xs">
                            <Clock className="w-3.5 h-3.5" />
                            {post.readTime}
                          </span>
                        </div>
                        <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900 group-hover:text-[#2563EB] transition-colors leading-tight">
                          {post.title}
                        </h2>
                        <p className="text-gray-500 mt-3 leading-relaxed text-sm">
                          {post.excerpt}
                        </p>
                        <span className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium mt-4 group-hover:gap-3 transition-all">
                          Läs mer <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
